import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HubspotRequestDemoModal from "@shared/HubspotRequestDemoModal";
import AiSummary from "@shared/AiAssistant/AiSummary";
import Button from "@shared/v2/Button";
import Toast from "@shared/Toast";
import { CheckSolidV6, HouseSolidV6, KeySolidV6, TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import viewPhone from "@shared/models/Communications/viewPhone";
import useSimpleNavigation from "@shared/v2/useSimpleNavigation";
import axios from "axios";
import {
  AddEditAddressModal,
  AddressModalCallbackProvider,
  useAddressModal,
  useAddressModalCallback,
} from "@shared/AddressModal";
import { isUSCountry } from "@shared/v2/utils";
import InvalidAddressModal from "./InvalidAddressModal";
import VerifyAddressModal from "./VerifyAddressModal";
import * as actions from "../actions/personDetailActionCreators";
import * as creators from "../../AddMarketReportModal/actions/creators";
import PersonInteractions from "./PersonInteractions";
import PersonInfo from "./PersonInfo";
import AssignedTo from "./assigned_to/AssignedTo";
import Details from "./Details";
import ContactInfo from "./ContactInfo";
import Documents from "./Documents";
import Relationships from "./Relationships";
import Transactions from "./Transactions";
import WebActivity from "./WebActivity";
import HomeApp from "./HomeApp";
import AutoPlans from "./auto_plans/AutoPlans";
import ListingAlertCard from "./listing_alerts/ListingAlertCard";
import MarketReports from "./MarketReports";
import CmaReports from "./CmaReports";
import CardTasks from "./tasks/CardTasks";
import CardAppointments from "./tasks/CardAppointments";
import Projects from "./Projects";
import Mortgage from "./Mortgage";
import AddMarketReportModal from "../../AddMarketReportModal";
import CardNotes from "./notes/CardNotes";
import TimelinesApp from "../../Timelines/components/TimelinesApp";
import PersonDetailsModal from "../../Contacts/PersonDetailsModal";
import ContactMergeModal from "../../ContactMergeModal/ContactMergeModal";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import AgreementsConfirmModal from "./AgreementsConfirmModal";
import Agreements from "./Agreements";
import ReferLenderModal from "./ReferLenderModal";
import { useSuccessMessageOpen } from "../../reducers/personalDetailReducer/personDetailReducer";
import SelectDocTypeModal, { DocButton } from "./modals/SelectDocTypeModal";
import { viewMarketReportApi } from "../../AddMarketReportModal/services";
import useIsEditable from "../shared/useIsEditable";
import { getHasDuplicates } from "../../reducers/contactMergeReducer";
import ForbiddenError from "./ForbiddenError";
import Marketing from "./Marketing";

const getApiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "x-csrf-token": window.ReactOnRails.authenticityToken(),
});

const PersonDetail = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  const { pathname, push } = useSimpleNavigation();
  const editPersonModalOpen = useSelector((state) => state.personDetail.editPersonModalOpen);
  const person = useSelector((state) => state.personDetail.person);
  const error = useSelector((state) => state.personDetail.error);
  const featureFlags = useFeatureFlags();
  const [state, _setState] = useState({
    platformCTAOpen: false,
    marketReportsRefreshKey: 0,
    collectionNotificationToast: {
      show: false,
      type: "",
      msg: "",
    },
  });
  const setState = (newState) =>
    _setState((prev) => ({ ...prev, ...(typeof newState === "function" ? newState(prev) : newState) }));
  const successMessageOpen = useSuccessMessageOpen();
  const isEditable = useIsEditable();
  const primaryPhoneNumber = useMemo(
    () =>
      (person.included?.filter((i) => i.type === "phone_detail").map((i) => viewPhone(i)) || []).find(
        (i) => i.isPrimary,
      ),
    [person.included],
  );

  const marketReportPerson = useMemo(
    () => ({
      ...person,
      emails: person.included
        ?.filter((i) => i.type === "email_detail")
        .map((i) => ({
          address: i.attributes.value,
          category: i.attributes.detailable_type,
        })),
      addresses: person.included
        ?.filter((i) => i.type === "address" && i.id !== null)
        .map(
          (i) =>
            `${i.attributes?.street_address} ${i.attributes?.city}, ${i.attributes?.locality} ${i.attributes?.postal_code}`,
        ),
    }),
    [person.data],
  );

  const loadData = () => {
    const slug = pathname.split("/").pop();
    dispatch(actions.getPerson(slug));
    dispatch(actions.getNavLinks(slug));
    dispatch(actions.fetchProjects(slug));
  };

  useEffect(() => {
    loadData();
  }, [pathname]);

  useEffect(() => {
    if (currentUser.account.isAdmin && isEditable && person.data) {
      dispatch(getHasDuplicates(person.data.attributes.slug));
    }
  }, [currentUser, isEditable, person.data]);

  const openPanel2 = async (marketReportId) => {
    const onFailure = (err) => dispatch(creators.getMarketReportError({ error: err }));
    const onSuccess = (response) => {
      if (response?.data?.error) {
        return onFailure(response?.data?.error);
      }
      return dispatch(creators.getMarketReportSuccess({ data: response.data }, 0));
    };
    const data = { id: marketReportId, primary_agent_uuid: person.data?.attributes.primary_agent_uuid };

    await viewMarketReportApi({ data, successCb: onSuccess, errorCb: onFailure });

    setState({ editMarketReportId: marketReportId, showAddModal: true });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const marketReportIdParam = urlParams.get("market_report_id");
    if (marketReportIdParam && person.data?.attributes.primary_agent_uuid) {
      openPanel2(marketReportIdParam);
    }
  }, [person.data?.attributes.primary_agent_uuid]);

  useEffect(() => {
    if (!person.data?.id) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("edit")) {
      dispatch(actions.openEditPersonModal());
    }
  }, [person.data?.id]);

  useEffect(() => {
    const firstName = person.data?.attributes.first_name || "";
    const lastName = person.data?.attributes.last_name || "";
    document.title = `${firstName} ${lastName}`.trim() || "No Name";
  }, [person.data?.attributes.first_name, person.data?.attributes.last_name]);

  const openPlatformCTA = () => setState({ platformCTAOpen: true });

  const onToggleCollectionNotification = ({ show, type, msg }) =>
    setState({ collectionNotificationToast: { show, type, msg } });

  const closePlatformCTA = () => setState({ platformCTAOpen: false });

  const editMarketReport = (id) => setState({ editMarketReportId: id, showAddModal: !!id });

  const toggleAddMarketModal = () => {
    dispatch(creators.setTab(0));
    setState((prevState) => ({
      marketReportsRefreshKey: prevState.showAddModal
        ? prevState.marketReportsRefreshKey + 1
        : prevState.marketReportsRefreshKey,
      showAddModal: !prevState.showAddModal,
      editMarketReportId: null,
    }));
  };

  const hiddenUploadInput = useRef(null);
  const [docType, setDocType] = useState("");
  const [openAddAgreementModal, setOpenAddAgreementModal] = useState(false);
  const [openSelectDocModal, setOpenSelectDocModal] = useState(false);
  const [openAgreementModalConfirmation, setOpenAgreementModalConfirmation] = useState(false);
  const [openAgreementTypeModal, setOpenAgreementTypeModal] = useState(false);

  const setDoc = (documentType) => {
    setDocType(documentType);
    if (documentType === "other") {
      hiddenUploadInput.current?.click();
    } else {
      setOpenAddAgreementModal(true);
    }
    setOpenSelectDocModal();
  };

  const getAppointments = () => dispatch(actions.getAppointments(person.data.attributes.uuid, 3));

  const agreementCloseModal = () => {
    setOpenAddAgreementModal(false);
    getAppointments();
  };

  const closeSelectDocModal = () => {
    setOpenAgreementTypeModal(false);
    getAppointments();
  };

  const primaryAgent = person.data?.attributes.primary_agent_uuid;
  const [hasApprovedMls, setHasApprovedMls] = useState(false);
  const [loadingReports, setLoadingReports] = useState(true);
  useEffect(() => {
    if (!person.data?.id) return;
    axios
      .get(`/market_reports/user_mlses?primary_agent_uuid=${primaryAgent}`, { headers: getApiHeaders() })
      .then(({ data }) => {
        setHasApprovedMls(data.length > 0);
        setLoadingReports(false);
        if (data?.[0]?.country) {
          dispatch(creators.setFormData({ mls_country: data[0].country }));
        }
      });
  }, [person.data?.id, state.marketReportsRefreshKey]);

  if (error?.error === "Forbidden") return <ForbiddenError onRefresh={loadData} error={error} />;

  return (
    <AddressModalCallbackProvider>
      <PersonDetailAddressModal />
      <PersonDetailInvalidAddressModal />
      <PersonDetailVerifyAddressModal />
      <div className="tw-overflow-auto tw-h-full tw-relative">
        <AgreementsConfirmModal
          show={openAgreementModalConfirmation}
          handleHide={() => {
            getAppointments();
            setOpenAgreementModalConfirmation(false);
          }}
          handleAddAgreement={() => {
            setOpenAgreementModalConfirmation(false);
            if (docType === "both") {
              setOpenAgreementTypeModal(true);
              return;
            }

            setDoc(docType);
          }}
        />
        <SelectDocTypeModal closeModal={closeSelectDocModal} modalOpen={openAgreementTypeModal}>
          <DocButton
            text="Buyer Agreement"
            doctype="buyer"
            Icon={KeySolidV6}
            onClickFunc={(doc) => {
              setDoc(doc);
              closeSelectDocModal();
            }}
          />
          <DocButton
            text="Seller Agreement"
            doctype="seller"
            Icon={HouseSolidV6}
            onClickFunc={(doc) => {
              setDoc(doc);
              closeSelectDocModal();
            }}
          />
        </SelectDocTypeModal>
        {successMessageOpen && (
          <Toast containerClassName="tw-w-[226px]" text="Referral submitted successfully!" />
        )}
        {state.collectionNotificationToast.show && (
          <Toast
            text={state.collectionNotificationToast.msg}
            containerClassName={`${state.collectionNotificationToast.show ? "tw-mt-[-100px] tw-opacity-100" : "tw-mt-[-80px] tw-opacity-0"} tw-p-12px tw-z-[9991] tw-transform tw--translate-x-1/2 tw-transition-all tw-duration-500
                ${state.collectionNotificationToast.type === "error" ? "tw-bg-[#FAE6E6] tw-border-[#FAC5C3]" : "tw-bg-[#EDFAF1] tw-border-[#CAF0D6]"}`}
            textClassName={
              state.collectionNotificationToast.type === "error" ? "tw-text-[#E5342E]" : "tw-text-[#177335]"
            }
            iconClassName={
              state.collectionNotificationToast.type === "error" ? "tw-text-[#E5342E]" : "tw-text-[#177335]"
            }
            Icon={
              state.collectionNotificationToast.type === "error" ? TriangleExclamationSolidV6 : CheckSolidV6
            }
          />
        )}
        {featureFlags.lenderReferral && <ReferLenderModal />}
        <div className="tw-max-w-[1650px] tw-mx-auto tw-flex tw-p-[16px] tw-gap-[16px]">
          <div id="person-details" className="tw-min-w-[280px] tw-max-w-[375px] tw-flex-1">
            <PersonInfo />
            <ContactInfo />
            <Details />
            <Relationships />
            <CardNotes />
            <Transactions />
            <Documents
              docType={docType}
              openAddAgreementModal={openAddAgreementModal}
              setDoc={setDoc}
              agreementCloseModal={agreementCloseModal}
              openSelectDocModal={openSelectDocModal}
              setOpenSelectDocModal={setOpenSelectDocModal}
              hiddenUploadInput={hiddenUploadInput}
            />
            <Projects />
          </div>

          <div className="tw-min-w-[654px] tw-flex-1 tw-flex tw-flex-col tw-gap-[16px]">
            {error && (
              <div className="tw-bg-semantic-red-5 tw-p-[12px] tw-flex tw-justify-between tw-gap-[8px]">
                <span>Something went wrong. Please try again.</span>{" "}
                <Button onClick={loadData}>Refresh</Button>
              </div>
            )}

            <AiSummary
              leadPersonUuid={person.data?.attributes?.uuid}
              phoneStatus={primaryPhoneNumber?.aiStatus || "missing"}
            />

            <PersonInteractions
              setOpenAddAgreementModal={(type) => {
                setDocType(type);
                setOpenAgreementModalConfirmation(true);
              }}
            />

            <div className="tw-shadow-tdp-card col-xs-12">
              <TimelinesApp newDetailsPage />
            </div>
          </div>

          <div className="tw-min-w-[280px] tw-max-w-[375px] tw-flex-1">
            <AssignedTo />
            {featureFlags.lenderReferral && <Mortgage />}
            <WebActivity openPlatformCTA={openPlatformCTA} />
            <Agreements />
            <CardAppointments />
            {isUSCountry(currentUser?.account?.country) && <Marketing />}
            <HomeApp />
            <CardTasks
              setOpenAddAgreementModal={(type) => {
                setDocType(type);
                setOpenAgreementModalConfirmation(true);
              }}
            />
            <AutoPlans openPlatformCTA={openPlatformCTA} />
            <ListingAlertCard
              onToggleCollectionNotification={onToggleCollectionNotification}
              openPlatformCTA={openPlatformCTA}
            />
            <MarketReports
              refreshKey={state.marketReportsRefreshKey}
              editMarketReport={(id) => editMarketReport(id)}
              editMarketReportPanel2={openPanel2}
              openMarketReport={() => toggleAddMarketModal()}
              hasApprovedMls={hasApprovedMls}
              loading={loadingReports}
            />
            <CmaReports hasApprovedMls={hasApprovedMls} loading={loadingReports} />
          </div>

          <PersonDetailsModal
            show={editPersonModalOpen}
            personId={person.data?.attributes.id}
            modalType={person?.data?.attributes.status === "brivity-user" ? "user" : "lead"}
            onClose={() => {
              dispatch(actions.closeEditPersonModal());
              dispatch(actions.getPersonDetails(person.data?.attributes.slug));
            }}
          />

          <HubspotRequestDemoModal show={state.platformCTAOpen} onClose={closePlatformCTA} />

          {state.showAddModal && (
            <AddMarketReportModal
              person={marketReportPerson}
              isOpen
              close={toggleAddMarketModal}
              primaryAgent={primaryAgent}
              id={state.editMarketReportId}
            />
          )}
          <ContactMergeModal push={push} />
        </div>
      </div>
    </AddressModalCallbackProvider>
  );
};

const PersonDetailAddressModal = () => {
  const addressModal = useAddressModal("addEdit");
  const { getOnSubmit } = useAddressModalCallback();
  const currentUser = useCurrentUser();

  return (
    <AddEditAddressModal
      initAddress={addressModal.modalState.initAddress}
      show={addressModal.isOpen}
      onClose={addressModal.close}
      usedAddresses={addressModal.modalState.usedAddresses}
      onSubmit={getOnSubmit()}
      title={addressModal.modalState.title}
      showArmedForcesOptions={currentUser?.account?.country?.trim() === "US"}
    />
  );
};

const PersonDetailInvalidAddressModal = () => {
  const invalidModal = useAddressModal("invalid");
  const { getInvalidOnSubmit, getInvalidOnEdit } = useAddressModalCallback();

  return (
    <InvalidAddressModal
      address={invalidModal.modalState.address}
      show={invalidModal.isOpen}
      onClose={invalidModal.close}
      onSubmit={getInvalidOnSubmit()}
      onEdit={getInvalidOnEdit()}
    />
  );
};

const PersonDetailVerifyAddressModal = () => {
  const verifyModal = useAddressModal("verify");
  const { getVerifyOnSubmit, getVerifyOnEdit } = useAddressModalCallback();

  return (
    <VerifyAddressModal
      originalAddress={verifyModal.modalState.originalAddress}
      suggestedAddress={verifyModal.modalState.suggestedAddress}
      show={verifyModal.isOpen}
      onClose={verifyModal.close}
      onSubmit={getVerifyOnSubmit()}
      onEdit={getVerifyOnEdit()}
    />
  );
};

export default PersonDetail;
